/**
 * SpeechExpressController.js
 *
 * SignalR-Integration für Recognosco SpeechExpress (Direktdiktat).
 * Läuft parallel zu FluencyDirectController (Sprachbefehle).
 */

import { HubConnectionBuilder, HttpTransportType, HubConnectionState } from '@microsoft/signalr';
import { insertRecognisedText, isEditableElement } from './TextInsertionUtil';

const SPEECHEXPRESS_HUB_URL = process.env.REACT_APP_SPEECHEXPRESS_URL || 'http://localhost:5100';
const SPEECHEXPRESS_ENABLED = process.env.REACT_APP_SPEECHEXPRESS_ENABLED !== 'false';
const TARGET_NAME = 'ReportDashboard';
const DISCONNECT_DEBOUNCE_MS = 250;

/** @type {import('@microsoft/signalr').HubConnection | null} */
let connection = null;
/** @type {HTMLElement | null} */
let currentTargetElement = null;
/** @type {{ targetId: string, name: string, processId: null, directDictationEnabled: boolean } | null} */
let currentTargetInfo = null;
let isRecording = false;
let focusOutTimer = null;
let initialized = false;
let initCount = 0;
let connectionGeneration = 0;
/** @type {Promise<void> | null} */
let pendingStart = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let disconnectTimer = null;
let focusListenersAttached = false;

/** @type {'disconnected' | 'reconnecting' | 'connected' | 'target_claimed'} */
let connectionStatus = 'disconnected';

/** @type {Set<(status: string) => void>} */
const statusListeners = new Set();

function notifyStatusListeners() {
  const status = getSpeechExpressStatus();
  statusListeners.forEach((listener) => {
    try {
      listener(status);
    } catch (e) {
      console.warn('[SpeechExpress] Status-Listener Fehler:', e);
    }
  });
}

function resetInactivityTracker() {
  try {
    sessionStorage.setItem('lastActivity', Date.now().toString());
  } catch (_) {
    // ignore
  }
}

/**
 * @returns {'disconnected' | 'reconnecting' | 'connected' | 'target_claimed' | 'recording'}
 */
export function getSpeechExpressStatus() {
  if (!SPEECHEXPRESS_ENABLED) return 'disconnected';
  if (!connection || connection.state === HubConnectionState.Disconnected) {
    return connectionStatus === 'reconnecting' ? 'reconnecting' : 'disconnected';
  }
  if (connection.state === HubConnectionState.Reconnecting) {
    return 'reconnecting';
  }
  if (connection.state === HubConnectionState.Connecting) {
    return 'reconnecting';
  }
  if (isRecording) return 'recording';
  if (currentTargetElement && currentTargetInfo) return 'target_claimed';
  if (connection.state === HubConnectionState.Connected) return 'connected';
  return 'disconnected';
}

/**
 * @param {(status: string) => void} callback
 * @returns {() => void}
 */
export function subscribeSpeechExpressStatus(callback) {
  statusListeners.add(callback);
  callback(getSpeechExpressStatus());
  return () => statusListeners.delete(callback);
}

function setConnectionStatus(status) {
  connectionStatus = status;
  notifyStatusListeners();
}

function createTargetInfo() {
  return {
    targetId: crypto.randomUUID(),
    name: TARGET_NAME,
    processId: null,
    directDictationEnabled: true,
  };
}

async function releaseTarget() {
  if (!connection || connection.state !== HubConnectionState.Connected) {
    currentTargetElement = null;
    currentTargetInfo = null;
    notifyStatusListeners();
    return;
  }

  try {
    await connection.invoke('ReleaseTargetAsync');
    console.log('[SpeechExpress] Target freigegeben');
  } catch (error) {
    console.warn('[SpeechExpress] ReleaseTargetAsync fehlgeschlagen:', error);
  } finally {
    currentTargetElement = null;
    currentTargetInfo = null;
    notifyStatusListeners();
  }
}

async function claimTargetForElement(element) {
  if (!connection || connection.state !== HubConnectionState.Connected) return;
  if (!isEditableElement(element)) return;

  if (currentTargetElement === element) return;

  if (currentTargetInfo) {
    await releaseTarget();
  }

  const targetInfo = createTargetInfo();
  try {
    const claimed = await connection.invoke('ClaimTargetAsync', targetInfo);
    if (claimed) {
      currentTargetElement = element;
      currentTargetInfo = targetInfo;
      console.log('[SpeechExpress] Target geclaimt:', targetInfo.targetId, element.id || element.tagName);
    } else {
      console.warn('[SpeechExpress] ClaimTargetAsync zurückgewiesen');
    }
  } catch (error) {
    console.warn('[SpeechExpress] ClaimTargetAsync fehlgeschlagen:', error);
  }
  notifyStatusListeners();
}

function handleFocusIn(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || !isEditableElement(target)) return;

  if (focusOutTimer) {
    clearTimeout(focusOutTimer);
    focusOutTimer = null;
  }

  claimTargetForElement(target);
}

function handleFocusOut(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (currentTargetElement !== target) return;

  if (focusOutTimer) clearTimeout(focusOutTimer);
  focusOutTimer = setTimeout(() => {
    focusOutTimer = null;
    const active = document.activeElement;
    if (active && currentTargetElement && active === currentTargetElement) {
      return;
    }
    if (active instanceof HTMLElement && isEditableElement(active)) {
      claimTargetForElement(active);
      return;
    }
    releaseTarget();
  }, 100);
}

function handleTextRecognised(content, consumeLeadingSpaces, trailingSpacesCount) {
  resetInactivityTracker();

  const element = currentTargetElement || document.activeElement;
  if (!(element instanceof HTMLElement) || !isEditableElement(element)) {
    console.warn('[SpeechExpress] Kein editierbares Ziel für Diktat');
    return;
  }

  const inserted = insertRecognisedText(element, content, {
    consumeLeadingSpaces,
    trailingSpacesCount,
  });

  if (inserted) {
    console.log('[SpeechExpress] Diktat eingefügt:', content.slice(0, 40));
  }
}

function registerHubHandlers(conn) {
  conn.on('TextRecognisedAsync', handleTextRecognised);

  conn.on('RecordingStartedAsync', () => {
    isRecording = true;
    resetInactivityTracker();
    console.log('[SpeechExpress] Aufnahme gestartet');
    notifyStatusListeners();
  });

  conn.on('RecordingStoppedAsync', () => {
    isRecording = false;
    console.log('[SpeechExpress] Aufnahme gestoppt');
    notifyStatusListeners();
  });

  conn.on('TargetChangedAsync', (target) => {
    console.log('[SpeechExpress] TargetChangedAsync:', target);
    notifyStatusListeners();
  });

  conn.on('ReportingStartedAsync', (target) => {
    console.log('[SpeechExpress] ReportingStartedAsync:', target);
  });

  conn.on('ReportingEndedAsync', () => {
    console.log('[SpeechExpress] ReportingEndedAsync');
    isRecording = false;
    notifyStatusListeners();
  });

  conn.onreconnecting(() => {
    setConnectionStatus('reconnecting');
    console.log('[SpeechExpress] Verbindung wird wiederhergestellt...');
  });

  conn.onreconnected(() => {
    setConnectionStatus('connected');
    console.log('[SpeechExpress] Verbindung wiederhergestellt');
    if (currentTargetElement && isEditableElement(currentTargetElement)) {
      claimTargetForElement(currentTargetElement);
    }
  });

  conn.onclose((error) => {
    if (conn !== connection) return;
    setConnectionStatus('disconnected');
    currentTargetElement = null;
    currentTargetInfo = null;
    isRecording = false;
    if (error) {
      console.warn('[SpeechExpress] Verbindung geschlossen:', error);
    }
    notifyStatusListeners();
  });
}

function attachFocusListeners() {
  if (focusListenersAttached) return;
  document.addEventListener('focusin', handleFocusIn, true);
  document.addEventListener('focusout', handleFocusOut, true);
  focusListenersAttached = true;
}

function detachFocusListeners() {
  if (!focusListenersAttached) return;
  document.removeEventListener('focusin', handleFocusIn, true);
  document.removeEventListener('focusout', handleFocusOut, true);
  focusListenersAttached = false;
  if (focusOutTimer) {
    clearTimeout(focusOutTimer);
    focusOutTimer = null;
  }
}

async function startConnection() {
  const generation = ++connectionGeneration;

  if (
    connection &&
    (connection.state === HubConnectionState.Connected ||
      connection.state === HubConnectionState.Connecting ||
      connection.state === HubConnectionState.Reconnecting)
  ) {
    return;
  }

  const conn = new HubConnectionBuilder()
    .withUrl(SPEECHEXPRESS_HUB_URL, {
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
    })
    .withAutomaticReconnect()
    .build();

  connection = conn;
  registerHubHandlers(conn);

  pendingStart = conn.start();
  try {
    await pendingStart;
    if (generation !== connectionGeneration || conn !== connection) {
      return;
    }
    setConnectionStatus('connected');
    console.log('[SpeechExpress] Verbunden mit', SPEECHEXPRESS_HUB_URL);
  } catch (error) {
    if (generation !== connectionGeneration) {
      return;
    }
    setConnectionStatus('disconnected');
    console.warn('[SpeechExpress] Verbindung fehlgeschlagen (SpeechExpress läuft evtl. nicht):', error.message);
  } finally {
    if (generation === connectionGeneration) {
      pendingStart = null;
    }
  }
}

async function performDisconnect() {
  if (initCount > 0) {
    return;
  }

  connectionGeneration += 1;
  detachFocusListeners();
  await releaseTarget();

  const conn = connection;
  connection = null;

  if (conn) {
    if (pendingStart) {
      try {
        await pendingStart;
      } catch (_) {
        // Start wurde abgebrochen – erwartbar beim Shutdown
      }
      pendingStart = null;
    }

    try {
      if (conn.state !== HubConnectionState.Disconnected) {
        await conn.stop();
      }
    } catch (error) {
      console.warn('[SpeechExpress] Stop fehlgeschlagen:', error);
    }
  }

  initialized = false;
  isRecording = false;
  setConnectionStatus('disconnected');
  console.log('[SpeechExpress] Verbindung getrennt');
}

/**
 * Initialisiert die SpeechExpress SignalR-Verbindung.
 * @returns {boolean}
 */
export function initSpeechExpress() {
  if (!SPEECHEXPRESS_ENABLED) {
    console.log('[SpeechExpress] Deaktiviert (REACT_APP_SPEECHEXPRESS_ENABLED=false)');
    return false;
  }

  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }

  initCount += 1;

  if (initialized) {
    notifyStatusListeners();
    return true;
  }

  initialized = true;
  attachFocusListeners();
  startConnection();

  window._speechExpressStatus = getSpeechExpressStatus;
  console.log('[SpeechExpress] Controller initialisiert');
  return true;
}

/**
 * Trennt die SpeechExpress-Verbindung und gibt Ressourcen frei.
 * Debounced, damit React Strict Mode die Verbindung nicht sofort wieder kappt.
 */
export function disconnectSpeechExpress() {
  initCount = Math.max(0, initCount - 1);
  if (initCount > 0) {
    return Promise.resolve();
  }

  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
  }

  return new Promise((resolve) => {
    disconnectTimer = setTimeout(async () => {
      disconnectTimer = null;
      await performDisconnect();
      resolve();
    }, DISCONNECT_DEBOUNCE_MS);
  });
}

export default initSpeechExpress;
